"use client";

import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  IconButton,
} from "@dafiti/closet-ui";
import { Close } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";
import { ReactNode } from "react";

import { useKeyboardInset } from "../../../hooks/useKeyboardInset.hook";
import { QuickFilterFooter } from "./QuickFilterFooter";

interface QuickFilterMobileDrawerProps {
  label: string;
  containerId: string;
  isOpen: boolean;
  onClear: () => void;
  onApply: () => void;
  onClose: () => void;
  children?: ReactNode;
}

export function QuickFilterMobileDrawer({
  label,
  containerId,
  isOpen,
  onClear,
  onApply,
  onClose,
  children,
}: QuickFilterMobileDrawerProps) {
  const t = useTranslations("catalog");
  // When the keyboard opens (price inputs), resize the sheet to the area above
  // the keyboard instead of letting it sit behind it. We lift it to the keyboard
  // top (`bottom: inset`, which already accounts for iOS page scroll via
  // offsetTop) and cap its height to the exact visible area (`visualViewport
  // .height`, more reliable than dvh on iOS), so it never overflows off-screen.
  // The flex column keeps the inputs on top, the radio list scrollable, and the
  // footer fixed at the bottom — always above the keyboard, never over the inputs.
  // vaul's repositionInputs is disabled so it doesn't fight this.
  const { inset, height } = useKeyboardInset(isOpen);
  const contentStyle = inset
    ? { bottom: inset, maxHeight: `${height}px` }
    : undefined;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      repositionInputs={false}
    >
      <DrawerContent
        aria-describedby={undefined}
        className="flex flex-col pb-0"
        data-testid={`quick-filter-drawer-${containerId}`}
        style={contentStyle}
      >
        <DrawerHeader className="flex shrink-0 items-center justify-between mb-nano">
          <DrawerTitle className="text-heading-5 font-bold text-gray-600">
            {label}
          </DrawerTitle>
          <DrawerClose asChild>
            <IconButton
              variant="subtle"
              size="md"
              className="size-8 [&_svg]:size-4"
              aria-label={t("drawer.closeFilter")}
              data-testid={`quick-filter-close-${containerId}`}
            >
              <Close aria-hidden="true" />
            </IconButton>
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody
          id={containerId}
          className="flex flex-1 flex-col min-h-0 mb-0"
          role="dialog"
          aria-label={t("drawer.facetDialog", { label })}
        >
          {children}
        </DrawerBody>

        <DrawerFooter className="shrink-0">
          <QuickFilterFooter onClear={onClear} onApply={onApply} size="lg" />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
